import React, { useEffect } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { Link, useSearchParams } from 'react-router-dom'
import { URL_APP } from 'helpers/config'
import AuthWithGoogleButton from 'components/pages/login/buttons/AuthWithGoogleButton'
import LoginWithLinkedInButton from 'components/pages/login/buttons/LoginWithLinkedInButton'
import LoginWithSlackButton from 'components/pages/login/buttons/LoginWithSlackButton'
import LoginPageEmail from 'components/pages/login/LoginPageEmail'
import OrAuthSection from 'components/pages/login/OrAuthSection'
import axios from 'providers/axios'
import useShowError from 'hooks/useShowError'
import NewAuthTemplate from 'components/templates/NewAuthTemplate'
import SpinningLogo from 'components/pages/login/SpinningLogo'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SetupPasskey from 'components/pages/login/SetupPasskey'
import { AuthDeviceInfo, LoginStatusResponse } from 'hooks/useRedirectIfLoggedIn'

const LoginPage = () => {
  const showError = useShowError()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openOtherOptions, setOpenOtherOptions] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [authDevices, setAuthDevices] = React.useState<AuthDeviceInfo[]>([])
  const [showPasskeySetup, setShowPasskeySetup] = React.useState(false)
  const [dialogDeviceId, setDialogDeviceId] = React.useState('')
  const [dialogRedirectUrl, setDialogRedirectUrl] = React.useState('')
  const redirectUrl = searchParams.get('redirectUrl')
  const requester = searchParams.get('requester') || ''
  const isChromeExtension = requester === 'chrome-extension'

  // Set default redirectUrl
  React.useEffect(() => {
    if (redirectUrl) return
    setSearchParams({ redirectUrl: URL_APP, requester })
  }, [redirectUrl, setSearchParams, requester])

  // Redirect if user is logged in
  useEffect(() => {
    if (!redirectUrl) return
    const checkLoginStatus = async () => {
      try {
        setLoading(true)
        if (isChromeExtension) return
        const { data } = await axios.post<LoginStatusResponse>('/login/status', { redirectUrl })

        if (data.isLoggedIn) window.location.href = data.redirectUrl
        else setAuthDevices(data.authDevices)
      } catch (error) {
        showError(error)
      } finally {
        setLoading(false)
      }
    }
    checkLoginStatus()
  }, [redirectUrl, isChromeExtension, showError])

  const pageContent = (
    <Box width="100%" maxWidth="350px" mx="auto" maxHeight="100%" display={loading ? 'none' : 'block'}>
      <h1 style={{ fontSize: '18px', marginBottom: '28px' }}>Log in to NachoNacho</h1>
      {showPasskeySetup && <SetupPasskey deviceId={dialogDeviceId} redirectUrl={dialogRedirectUrl} />}
      {!showPasskeySetup && (
        <>
          <Box mb="16px">
            <AuthWithGoogleButton id="btn-login-google" textButton="Log In with Google" typeCreationApp="LOGIN" />
          </Box>

          {openOtherOptions ? (
            <Box>
              <Box mb="16px">
                <LoginWithLinkedInButton id="btn-login-linkedin" textButton="Log In with Linkedin" />
              </Box>

              <Box mb="16px">
                <LoginWithSlackButton id="btn-login-slack" textButton="Log In with Slack" />
              </Box>
            </Box>
          ) : (
            <Button id="btn-login-options" onClick={() => setOpenOtherOptions(true)}>
              Other log in options <ExpandMoreIcon sx={{ fontSize: '16px', ml: '2px', fill: '#1445fe' }} />
            </Button>
          )}

          <OrAuthSection />

          <LoginPageEmail
            authDevices={authDevices}
            useAnotherAccount={() => setAuthDevices([])}
            setLoading={setLoading}
            setPasskeyDialog={(deviceId, redirectUrl) => {
              setDialogDeviceId(deviceId)
              setDialogRedirectUrl(redirectUrl)
              setShowPasskeySetup(true)
            }}
          />

          <Box py="20px">
            <span className="fs-14">
              New to NachoNacho?{' '}
              <Link to="/signup" className="link">
                Sign up
              </Link>
            </span>
          </Box>
        </>
      )}
    </Box>
  )

  return (
    <NewAuthTemplate
      container1={{ content: <SpinningLogo /> }}
      container2={{
        content: (
          <>
            <CircularProgress size={50} sx={{ mx: 'auto', display: loading ? 'block' : 'none' }} />
            {pageContent}
          </>
        ),
      }}
    />
  )
}

export default LoginPage
